import { useEffect, useState } from "react";
import styles from "@/components/library/BookDetailVisual.module.css";
import { useFlash } from "@/hooks/useFlash";
import { useI18n } from "@/i18n";
import { listInsights, applyInsight, deleteInsight } from "@/lib/insightStore";
import { ApplyDecisionSheet, AppliedMark } from "@/components/library/ApplyDecisionSheet";

export const INSIGHTS_COPY = {
  id: {
    title: "Insight tersimpan",
    empty: "Belum ada insight untuk buku ini. Simpan dari poin ringkasan di atas.",
    apply: "Terapkan",
    deleting: "Menghapus...",
    delete: "Hapus",
    confirmDelete: "Ketuk lagi untuk hapus",
    applied: "Diterapkan sebagai ASSUMED.",
    deleted: "Insight dihapus.",
    fail: "Operasi belum berhasil. Coba lagi.",
    retry: "Coba lagi",
    failLoad: "Insight belum dapat dimuat.",
    linked: "Sudah diterapkan",
  },
  en: {
    title: "Saved insights",
    empty: "No insights for this book yet. Save from a brief point above.",
    apply: "Apply",
    deleting: "Deleting...",
    delete: "Delete",
    confirmDelete: "Tap again to delete",
    applied: "Applied as ASSUMED.",
    deleted: "Insight deleted.",
    fail: "The operation did not succeed. Try again.",
    retry: "Try again",
    failLoad: "Insights could not be loaded.",
    linked: "Already applied",
  },
};

/**
 * Daftar insight buku ini + terapkan/hapus (L11).
 * Hapus memakai konfirmasi dua-ketuk inline (keputusan founder C).
 */
export function InsightList({ bookId, refreshKey, onChanged }) {
  const { lang } = useI18n();
  const copy = INSIGHTS_COPY[lang === "en" ? "en" : "id"];
  const [state, setState] = useState("loading");
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(null);
  const [arming, setArming] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [flash, setFlash] = useFlash(2000);
  const [failed, setFailed] = useState(false);

  const load = async () => {
    setState("loading");
    setFailed(false);
    const res = await listInsights();
    if (res.error) {
      setFailed(true);
      return setState("error");
    }
    setItems((res.insights ?? []).filter((i) => i.sourceRef?.bookId === bookId));
    setState("ready");
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, refreshKey]);

  const onApply = async (decisionId) => {
    if (!pending) return { error: "invalid" };
    const res = await applyInsight({ insightId: pending.id, decisionId });
    if (res.error) return res;
    setPending(null);
    setFlash(copy.applied);
    onChanged?.();
    await load();
    return {};
  };

  const onDelete = async (id) => {
    if (arming !== id) {
      setArming(id);
      return;
    }
    setArming(null);
    setBusyId(id);
    setFailed(false);
    const res = await deleteInsight(id);
    setBusyId(null);
    if (res.error) return setFailed(true);
    setFlash(copy.deleted);
    onChanged?.();
    await load();
  };

  if (state === "loading") {
    return (
      <section aria-busy="true" data-testid="insights-loading" className={styles.insightSection}>
        <p className={styles.briefLoading}>…</p>
      </section>
    );
  }

  if (state === "error") {
    return (
      <section role="alert" data-testid="insights-error" className={styles.insightSection}>
        <p className="m-0 text-sm text-ax-text-2">{copy.failLoad}</p>
        <div className="mt-3">
          <button type="button" onClick={load} data-testid="insights-retry-button" className={styles.briefAction}>
            {copy.retry}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="insights-section" className={styles.insightSection} aria-labelledby="insights-heading">
      <div className={styles.briefHeader}>
        <h2 id="insights-heading" className={styles.briefTitle} data-testid="insights-title">
          {copy.title}
        </h2>
        <span className={styles.briefBadge} data-testid="insights-count">{String(items.length)}</span>
      </div>
      <p role="status" data-testid="insights-flash" className={styles.briefFlash}>{flash}</p>
      {failed && <p role="alert" data-testid="insights-error-op" className={styles.briefError}>{copy.fail}</p>}
      {items.length === 0 && <p className="m-0 mt-3 text-sm text-ax-text-2" data-testid="insights-empty">{copy.empty}</p>}
      <ul className={styles.briefPoints}>
        {items.map((it) => (
          <li key={it.id} data-testid={`insight-card-${it.id}`} className={styles.briefPoint}>
            <p className={styles.briefPointText} data-testid={`insight-text-${it.id}`}>{it.text}</p>
            {it.note ? <p className="m-0 mt-1 text-sm text-ax-text-2">{it.note}</p> : null}
            <div className={styles.briefPointMeta}>
              {it.itemId ? (
                <AppliedMark testId={`insight-done-${it.id}`} />
              ) : (
                <button
                  type="button"
                  data-testid={`insight-apply-${it.id}`}
                  onClick={() => setPending(it)}
                  className={styles.briefAction}
                >
                  {copy.apply}
                </button>
              )}
              <button
                type="button"
                data-testid={`insight-delete-${it.id}`}
                disabled={busyId === it.id}
                onClick={() => onDelete(it.id)}
                className={`${styles.briefAction} ${styles.briefActionDanger} ${arming === it.id ? styles.armed : ""}`}
              >
                {busyId === it.id ? copy.deleting : arming === it.id ? copy.confirmDelete : copy.delete}
              </button>
            </div>
          </li>
        ))}
      </ul>

      <ApplyDecisionSheet
        open={pending !== null}
        onClose={() => setPending(null)}
        onApply={onApply}
        onApplied={() => {}}
        testPrefix="insight-apply"
      />
    </section>
  );
}
